import { CHAIN_IDS, CHAIN_IDS_SUPPORT_INTENTS } from '@/constants/chain';

import {
  matchaAutoSelectors,
  resolveIsMatchaAutoOn,
  useMarketSettingsHydrated,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { getIsIntentsSupported } from '@/utils/gas';

import { useTradeGeoStatus } from './useGeoCheck';
import { useIsSmartWallet } from './useIsSmartWallet';

type Props = {
  /** Buy token */
  buyToken?: TokenResult;
  /** Sell token */
  sellToken: TokenResult | undefined;
  /** Chain ID */
  chainId: number;
};

export const useIntentsAvailability = ({ buyToken, chainId, sellToken }: Props) => {
  const { isMatchaAutoOn } = useMarketTradeSettingsStore(matchaAutoSelectors);
  // Pre-hydration the store shows `undefined` even for users who explicitly
  // opted out, so the resolved default must wait for the persisted state.
  const hydrated = useMarketSettingsHydrated();
  const geoStatus = useTradeGeoStatus(buyToken, sellToken);
  const isSmartWallet = useIsSmartWallet({ chainId });
  const isIntentsSupported = sellToken
    ? getIsIntentsSupported(buyToken, sellToken)
    : CHAIN_IDS_SUPPORT_INTENTS.includes(chainId);
  const unavailableReason =
    geoStatus !== 'geo-allowed'
      ? geoStatus
      : isSmartWallet && chainId === CHAIN_IDS.MONAD
        ? 'monad-smart-wallet'
        : !isIntentsSupported
          ? 'unsupported'
          : undefined;
  const isAvailable = unavailableReason === undefined;

  return {
    isAvailable,
    isEnabled: hydrated && resolveIsMatchaAutoOn(isMatchaAutoOn, chainId) && isAvailable,
    unavailableReason,
  };
};
